// C.1 — резолвер таргетов из DSL v6.
//
// Принимает (state, sourceCard, targetKey, opts) → массив целей (юнитов
// и/или Героев) согласно ключу из DSL.md.
//
// Карты обычно дополнительно фильтруют (например, по фракции, по hp,
// по keyword). Резолвер делает только «грубый» отбор по правилу таргета.
//
// Не реализованные кейсы (пока выходят с пустым результатом + console.warn):
//   - composite — карты сами парсят composite-value.
//   - placement-time picker'ы (ally_choose, enemy_choose, и т.д.) для UI —
//     ниже возвращаем валидных кандидатов, выбор остаётся за UI/ботом
//     через opts.chosenTargets.
//   - target_if_* — условные таргеты, проверка condition внешняя.

const KNOWN_TARGETS = new Set([
  'self', 'hero_self', 'hero_self_no_overheal',
  'ally_choose', 'ally_random', 'ally_random_fallback_self', 'ally_random_other',
  'ally_lowest_hp', 'ally_strongest', 'ally_weakest',
  'ally_aoe_line', 'ally_aoe_all', 'ally_aoe_line_1', 'ally_aoe_line_2',
  'ally_aoe_chosen_line', 'ally_aoe_same_line',
  'ally_neighbors_line',
  'enemy_choose', 'enemy_or_hero_choose', 'enemy_or_ally_choose',
  'hero_enemy', 'enemy_random', 'enemy_random_or_hero', 'enemy_unit_random',
  'enemy_random_per_ally',
  'enemy_lowest_hp', 'enemy_strongest', 'enemy_highest_atk', 'enemy_healer',
  'enemy_aoe_line', 'enemy_aoe_all', 'enemy_aoe_line_1', 'enemy_aoe_line_2',
  'enemy_aoe_line_1_if_2plus', 'enemy_aoe_chosen_line', 'enemy_aoe_random',
  'enemy_suppressed_or_silenced', 'enemy_infected_aoe',
  'opposite_line', 'opposite_unit_same_cell', 'opposite_cells_line_2',
  'any_unit_within_own_side', 'event_subject', 'unit_attack_target',
  'attacker', 'self_carrier', 'self_owner',
  'target_if_ally', 'target_if_enemy', 'target_if_infected',
  // Этап 25: алиасы и синонимы для xlsx-target'ов
  'all_allies', 'all_units_on_field', 'allies_with_mod', 'ally_summoned',
  'own_discard'
]);

function getOwnerIdx(source) {
  return source?.ownerId ?? null;
}

function allUnitsOf(player) {
  const result = [];
  if (!player?.field) return result;
  for (let line = 0; line < 2; line++) {
    const row = player.field[line];
    if (!row) continue;
    for (const u of row) {
      if (u && (!u.isAlive || u.isAlive())) result.push(u);
    }
  }
  return result;
}

function unitsOnLine(player, line) {
  if (!player?.field?.[line]) return [];
  return player.field[line].filter(u => u && (!u.isAlive || u.isAlive()));
}

function randomFrom(state, list) {
  if (!list || list.length === 0) return null;
  if (state?.rng?.int) return list[state.rng.int(list.length)];
  return list[Math.floor(Math.random() * list.length)];
}

function pickByPriority(list, getter, mode = 'lowest') {
  if (!list || list.length === 0) return null;
  let best = list[0];
  let bestVal = getter(best);
  for (let i = 1; i < list.length; i++) {
    const v = getter(list[i]);
    if (mode === 'lowest' ? v < bestVal : v > bestVal) {
      best = list[i]; bestVal = v;
    }
  }
  return best;
}

// Главная функция. Возвращает МАССИВ целей (Heroes/units), пустой если
// нет валидных целей.
export function resolveTarget(state, sourceCard, targetKey, opts = {}) {
  if (!state || !sourceCard) return [];
  if (!targetKey) return [];
  const ownerIdx = getOwnerIdx(sourceCard);
  if (ownerIdx == null) return [];
  const enemyIdx = 1 - ownerIdx;
  const owner = state.players?.[ownerIdx];
  const enemy = state.players?.[enemyIdx];
  if (!owner || !enemy) return [];

  if (!KNOWN_TARGETS.has(targetKey)) {
    // composite / own_line / discard / hand / mod-targets — внешняя обработка.
    return [];
  }

  switch (targetKey) {
    case 'self':
      return [sourceCard];
    case 'self_carrier':
    case 'self_owner':
      return [sourceCard]; // упрощённо
    case 'hero_self':
    case 'hero_self_no_overheal':
      return owner.hero ? [owner.hero] : [];
    case 'hero_enemy':
      return enemy.hero ? [enemy.hero] : [];

    case 'ally_choose':
    case 'ally_choose_priority':
      // UI/бот должен передать opts.chosenTargets.
      if (opts?.chosenTargets) return [].concat(opts.chosenTargets);
      return allUnitsOf(owner); // кандидаты
    case 'ally_random':
      return [randomFrom(state, allUnitsOf(owner))].filter(Boolean);
    case 'ally_random_fallback_self':
      {
        const others = allUnitsOf(owner).filter(u => u !== sourceCard);
        const pick = randomFrom(state, others);
        return [pick || sourceCard];
      }
    case 'ally_random_other':
      {
        const others = allUnitsOf(owner).filter(u => u !== sourceCard);
        const pick = randomFrom(state, others);
        return pick ? [pick] : [];
      }
    case 'ally_lowest_hp':
      {
        const a = allUnitsOf(owner);
        const t = pickByPriority(a, u => u.current?.hp ?? Infinity, 'lowest');
        return t ? [t] : [];
      }
    case 'ally_strongest':
      {
        const a = allUnitsOf(owner);
        const t = pickByPriority(a, u => u.current?.atk ?? 0, 'highest');
        return t ? [t] : [];
      }
    case 'ally_weakest':
      {
        const a = allUnitsOf(owner);
        const t = pickByPriority(a, u => u.current?.atk ?? Infinity, 'lowest');
        return t ? [t] : [];
      }
    case 'ally_aoe_all':
      return allUnitsOf(owner);
    case 'ally_aoe_line':
    case 'ally_aoe_same_line':
      return unitsOnLine(owner, sourceCard.line ?? 0);
    case 'ally_aoe_line_1':
      return unitsOnLine(owner, 0);
    case 'ally_aoe_line_2':
      return unitsOnLine(owner, 1);
    case 'ally_aoe_chosen_line':
      {
        const line = opts?.chosenLine;
        if (line !== 0 && line !== 1) return [];
        return unitsOnLine(owner, line);
      }
    case 'ally_neighbors_line':
      {
        if (sourceCard.line == null || sourceCard.slot == null) return [];
        const row = owner.field?.[sourceCard.line] || [];
        const result = [];
        const left = row[sourceCard.slot - 1];
        const right = row[sourceCard.slot + 1];
        if (left) result.push(left);
        if (right) result.push(right);
        return result;
      }

    case 'enemy_choose':
    case 'enemy_or_hero_choose':
    case 'enemy_or_ally_choose':
      if (opts?.chosenTargets) return [].concat(opts.chosenTargets);
      return targetKey === 'enemy_or_hero_choose'
        ? [...allUnitsOf(enemy), enemy.hero].filter(Boolean)
        : allUnitsOf(enemy);
    case 'enemy_random':
      return [randomFrom(state, allUnitsOf(enemy))].filter(Boolean);
    case 'enemy_unit_random':
      return [randomFrom(state, allUnitsOf(enemy))].filter(Boolean);
    case 'enemy_random_or_hero':
      {
        const pool = [...allUnitsOf(enemy)];
        if (enemy.hero) pool.push(enemy.hero);
        return [randomFrom(state, pool)].filter(Boolean);
      }
    case 'enemy_random_per_ally':
      {
        const aliveAllies = allUnitsOf(owner);
        const enemies = allUnitsOf(enemy);
        const result = [];
        for (let i = 0; i < aliveAllies.length; i++) {
          const pick = randomFrom(state, enemies);
          if (pick) result.push(pick);
        }
        return result;
      }
    case 'enemy_lowest_hp':
      {
        const e = allUnitsOf(enemy);
        const t = pickByPriority(e, u => u.current?.hp ?? Infinity, 'lowest');
        return t ? [t] : [];
      }
    case 'enemy_strongest':
    case 'enemy_highest_atk':
      {
        const e = allUnitsOf(enemy);
        const t = pickByPriority(e, u => u.current?.atk ?? 0, 'highest');
        return t ? [t] : [];
      }
    case 'enemy_aoe_all':
      return allUnitsOf(enemy);
    case 'enemy_aoe_line':
      return unitsOnLine(enemy, sourceCard.line ?? 0);
    case 'enemy_aoe_line_1':
      return unitsOnLine(enemy, 0);
    case 'enemy_aoe_line_2':
      return unitsOnLine(enemy, 1);
    case 'enemy_aoe_line_1_if_2plus':
      {
        const list = unitsOnLine(enemy, 0);
        return list.length >= 2 ? list : [];
      }
    case 'enemy_aoe_chosen_line':
      {
        const line = opts?.chosenLine;
        if (line !== 0 && line !== 1) return [];
        return unitsOnLine(enemy, line);
      }
    case 'enemy_aoe_random':
      {
        const e = allUnitsOf(enemy);
        const t = randomFrom(state, e);
        return t ? [t] : [];
      }
    case 'enemy_infected_aoe':
      return allUnitsOf(enemy).filter(u => (u.infectedStacks || 0) > 0);
    case 'enemy_suppressed_or_silenced':
      return allUnitsOf(enemy).filter(u => u.current?.silenced || (u.keywords || []).includes('suppression'));

    case 'opposite_line':
      // юниты на противоположной линии напротив (зеркальная линия у врага).
      return unitsOnLine(enemy, sourceCard.line ?? 0);
    case 'opposite_unit_same_cell':
      {
        if (sourceCard.line == null || sourceCard.slot == null) return [];
        const target = enemy.field?.[sourceCard.line]?.[sourceCard.slot];
        return target ? [target] : [];
      }
    case 'opposite_cells_line_2':
      return unitsOnLine(enemy, 1);

    case 'event_subject':
    case 'attacker':
    case 'unit_attack_target':
      // Передаётся через opts.eventSubject / opts.attacker / opts.target.
      {
        const subject = opts?.eventSubject || opts?.attacker || opts?.target;
        return subject ? [subject] : [];
      }

    case 'any_unit_within_own_side':
      if (opts?.chosenTargets) return [].concat(opts.chosenTargets);
      return allUnitsOf(owner);

    case 'target_if_ally':
    case 'target_if_enemy':
    case 'target_if_infected':
      // Условные таргеты — внешняя проверка.
      return opts?.chosenTargets ? [].concat(opts.chosenTargets) : [];

    // Этап 25: алиасы из xlsx.
    case 'all_allies':
      return allUnitsOf(owner);
    case 'all_units_on_field':
      return [...allUnitsOf(owner), ...allUnitsOf(enemy)];
    case 'allies_with_mod':
      return allUnitsOf(owner).filter(u =>
        Array.isArray(u.modifications) && u.modifications.length > 0
      );
    case 'ally_summoned':
      // Передаётся через opts.eventSubject (новый призванный юнит).
      return opts?.eventSubject ? [opts.eventSubject] : [];
    case 'own_discard':
      // Возвращаем массив карт в своём сбросе (для add_extra_copy_to_discard).
      return Array.isArray(owner?.discard) ? owner.discard.slice() : [];

    default:
      return [];
  }
}

export function isValidTargetKey(key) {
  return KNOWN_TARGETS.has(key);
}
